/** Planer i RCP — teksty obszaru (`i18n/planner-grid.ts`): grafik i konfiguracja Planera pod `pln.grid.*`. */
type Shape<T> = { [K in keyof T]: T[K] extends string ? string : Shape<T[K]> };

export const plnGridPl = {
  hero: {
    description: 'Tydzień {week} · {range}',
    shifts: 'Zmian w tygodniu',
    draftsHint: 'w tym szkice: {n}',
    hours: 'Godzin pracy',
    tasks: 'Czynności',
    tasksHint: 'wykonane: {pct}%',
    warnings: 'Do sprawdzenia',
    warningsHint: 'konflikty: {conflicts} · czas pracy: {wt}',
    people: 'Osób',
    absences: 'Nieobecności',
    pending: 'Wnioski do decyzji',
  },
  actions: {
    addShift: 'Dodaj zmianę',
    absence: 'Nieobecność',
    templates: 'Szablony tygodnia',
    cloneWeek: 'Klonuj tydzień',
    requests: 'Wnioski',
    setup: 'Czynności i atrybuty',
    readOnly: 'Tylko podgląd',
  },
  views: { label: 'Widok', week: 'Tydzień', day: 'Dzień', month: 'Miesiąc', board: 'Tablica czynności' },
  nav: { prev: 'Wstecz', next: 'Dalej', today: 'Dziś', week: 'Tydz. {n}' },
  filters: {
    search: 'Szukaj osoby…',
    department: 'Dział',
    allDepartments: 'Wszystkie działy',
    people: 'Lista osób',
    peopleWorking: 'Pracujący w zakresie',
    peopleAll: 'Wszyscy',
    peopleCount: '{shown} z {total}',
    shiftFilter: 'Rodzaj wpisu',
    all: 'Wszystkie',
    absences: 'Nieobecności',
  },
  short: { MORNING: 'R', AFTERNOON: 'P', NIGHT: 'N', FULL_DAY: 'D' },
  week: {
    people: 'Osoby',
    coverage: 'Obsada',
    noShifts: 'brak zmian',
    holiday: 'Święto',
    selectAll: 'Zaznacz wszystkie komórki',
    clearAll: 'Wyczyść zaznaczenie',
    selectRow: 'Zaznacz osobę: {name}',
    selectCol: 'Zaznacz dzień: {day}',
    selectShift: 'Zaznacz zmianę {range}',
    addInCell: 'Dodaj zmianę: {name}, {day}',
    addAbsence: 'Nieobecność',
    moreTasks: '+ {n} więcej',
  },
  wtBadge: 'Czas pracy: {n}',
  day: {
    carried: 'z poprzedniego dnia',
    dragMove: 'Przeciągnij, aby przesunąć zmianę',
    dragResize: 'Przeciągnij, aby zmienić długość zmiany',
    taskDone: 'oznacz jako wykonaną',
    taskUndone: 'cofnij wykonanie',
  },
  month: {
    weekNo: 'Tydz.',
    openDay: '{day}: zmian {n} — otwórz dzień',
    openWeek: 'Otwórz tydzień {n}',
  },
  board: {
    person: 'Osoba',
    allPeople: 'Wszystkie osoby',
    count: 'Czynności: {n}',
    empty: 'Brak czynności',
    columns: { PENDING: 'Do zrobienia', IN_PROGRESS: 'W toku', DONE: 'Wykonane', OTHER: 'Pominięte i niewykonane' },
  },
  bulk: {
    label: 'Akcje na zaznaczeniu',
    cells: '{people} os. × {days} dni → {cells} komórek',
    selectedShifts: 'Zaznaczone zmiany: {n}',
    insert: 'Wstaw zmianę',
    retype: 'Zmień rodzaj',
    delete: 'Usuń',
    deleteInCells: 'Usuń zmiany',
    clear: 'Wyczyść',
    deleteTitle: 'Usunąć zmiany?',
    confirmDelete: 'Z grafiku zniknie {n} zmian razem z ich czynnościami.',
    noEmpty: 'W zaznaczeniu nie ma pustych komórek',
    noShifts: 'W zaznaczeniu nie ma zmian do usunięcia',
  },
  result: {
    saved: 'Zmiana zapisana',
    deletedOne: 'Zmiana usunięta',
    created: 'Dodano zmian: {n}',
    deleted: 'Usunięto zmian: {n}',
    updated: 'Zmieniono zmian: {n}',
    skipped: 'Pominięto: {n} ({reasons})',
    warnings: 'Zapisano z ostrzeżeniami czasu pracy: {n}',
    nothing: 'Nic się nie zmieniło',
    nothingToClone: 'W tym tygodniu nie ma zmian do sklonowania',
    cloneAllOccupied: 'Następny tydzień ma już wpisy we wszystkich tych komórkach — nic nie skopiowano',
    absenceSaved: 'Nieobecność zapisana',
    absenceSavedRemoved: 'Nieobecność zapisana · zdjęto z grafiku zmian: {n}',
    absenceDeleted: 'Nieobecność usunięta',
    absenceDeletedRestored: 'Nieobecność usunięta · przywrócono zmian: {n}',
    saveError: 'Nie udało się zapisać — spróbuj ponownie',
  },
  conflict: {
    VEHICLE_DOUBLE: 'Pojazd przydzielony do dwóch zmian w tym samym czasie',
    PERSON_OVERLAP: 'Ta osoba ma nakładające się zmiany',
    ABSENCE: 'Zmiana w dniu nieobecności',
    TASK_OUTSIDE_SHIFT: 'Czynność wychodzi poza godziny zmiany',
    TASK_OVERLAP: 'Czynności w zmianie nakładają się',
  },
  conflictShort: {
    VEHICLE_DOUBLE: 'Pojazd',
    PERSON_OVERLAP: '2× zmiana',
    ABSENCE: 'Nieobecność',
    TASK_OUTSIDE_SHIFT: 'Poza zmianą',
    TASK_OVERLAP: 'Nakładanie',
  },
  block: {
    intro: 'Serwer zatrzymał zapis. Zmień godziny albo zapisz mimo to — powód trafi do dziennika zmian.',
    placeholder: 'Np. zastępstwo za chorego pracownika, uzgodnione z kierownikiem',
    required: 'Podaj powód (co najmniej 3 znaki)',
    submit: 'Zapisz mimo naruszenia',
    change: 'Wróć i popraw',
  },
  shift: {
    titleNew: 'Nowa zmiana',
    titleEdit: 'Edycja zmiany',
    titleView: 'Zmiana',
    person: 'Osoba',
    personPick: 'Wybierz osobę',
    date: 'Dzień',
    type: 'Rodzaj zmiany',
    start: 'Początek',
    end: 'Koniec',
    nextDay: '(następnego dnia)',
    status: 'Status',
    duration: 'Czas zmiany: {h}:{m} h',
    fitHours: 'Dopasuj do czynności',
    vehicle: 'Pojazd',
    noVehicle: 'Bez pojazdu',
    attrNone: '— brak —',
    attrTextPh: 'Wpisz wartość',
    notes: 'Notatka',
    notesPh: 'Informacja dla pracownika (widoczna w aplikacji)',
    hints: 'Kontrola grafiku',
    hintsOk: 'Bez uwag — odpoczynek, czas pracy i przydziały w normie',
    save: 'Zapisz zmianę',
    delete: 'Usuń zmianę',
    confirmDelete: 'Zmiana zniknie z grafiku razem z czynnościami.',
    copy: 'Kopiuj na inne dni',
    asAbsence: 'To nieobecność',
  },
  task: {
    title: 'Czynności',
    empty: 'Brak czynności — dodaj z szablonu albo ręcznie',
    fromTemplate: 'Dodaj z szablonu…',
    manual: 'Dodaj ręcznie',
    name: 'Nazwa',
    nameRequired: 'Podaj nazwę czynności',
    type: 'Typ',
    priority: 'Priorytet',
    start: 'Godzina',
    duration: 'Czas (min)',
    status: 'Status',
    location: 'Miejsce',
    customer: 'Klient (opcjonalnie)',
    customerPh: 'Szukaj klienta…',
    notes: 'Opis',
    checklist: 'Checklista',
    checklistPh: 'Nowy punkt listy',
    checklistAdd: 'Dodaj punkt',
    checklistRemove: 'Usuń punkt: {label}',
    checklistCount: 'checklista {done}/{n}',
    add: 'Dodaj czynność',
    update: 'Zapisz czynność',
    edit: 'Edytuj: {title}',
    remove: 'Usuń: {title}',
  },
  copy: {
    title: 'Kopiuj zmianę',
    description: '{range} · czynności: {n}',
    person: 'Osoba docelowa',
    from: 'Od',
    to: 'Do',
    presets: { nextWeek: 'Następny tydzień', restMonth: 'Do końca miesiąca', next30: 'Najbliższe 30 dni' },
    dows: 'Dni tygodnia',
    count: 'Dni do skopiowania: {n}',
    vehicleNote: 'pojazd nie jest kopiowany',
    submit: 'Kopiuj ({n})',
    noDays: 'Zaznacz dni tygodnia i zakres dat',
    allOccupied: 'Wszystkie wybrane dni są już zajęte (zmiana albo nieobecność) — nic nie skopiowano',
    skipped: 'Pominięto zajęte dni: {n}',
  },
  absence: {
    title: 'Nowa nieobecność',
    detailsTitle: 'Nieobecność',
    from: 'Od',
    to: 'Do',
    code: 'Rodzaj',
    paid: 'Płatna — liczy się do wymiaru czasu pracy',
    unpaid: 'Bezpłatna — nie liczy się do wymiaru',
    notes: 'Uwagi',
    days: 'Dni: {n}',
    clashWarn: 'W tych dniach są zaplanowane zmiany ({n}) — zostaną zdjęte z grafiku i wrócą po usunięciu nieobecności.',
    save: 'Zapisz nieobecność',
    delete: 'Usuń nieobecność',
    confirmDelete: 'Nieobecność zniknie z grafiku, a zdjęte wtedy zmiany wrócą.',
    fromRequest: 'Z wniosku pracownika — pokaż wnioski',
  },
  requests: {
    title: 'Wnioski pracowników',
    filter: 'Status wniosków',
    empty: 'Brak wniosków',
    preferred: 'preferowana: {type}',
    createsAbsence: 'Po zatwierdzeniu dni trafią do grafiku jako: {label}',
    response: 'Odpowiedź',
    responsePh: 'Odpowiedź dla pracownika (opcjonalnie)',
    respondedBy: 'Rozpatrzył(a): {name}',
    approve: 'Zatwierdź',
    reject: 'Odrzuć',
    approved: 'Wniosek zatwierdzony',
    rejected: 'Wniosek odrzucony',
    clash: 'W tych dniach osoba ma zaplanowane zmiany ({n}). Zatwierdzenie zdejmie je z grafiku.',
    clashConfirm: 'Zatwierdź i zdejmij zmiany',
  },
  templates: {
    title: 'Szablony tygodnia',
    description: 'Obsadź tydzień {range} gotowym wzorcem — tylko puste komórki, jako szkice.',
    scope: 'Dla kogo',
    scopeVisible: 'Widoczne osoby ({n})',
    scopeSelected: 'Zaznaczone osoby ({n})',
    preview: 'Do dodania: {n} · zajętych pominiętych: {skipped}',
    apply: 'Zastosuj',
    items: {
      morningWeekdays: { label: 'Ranna, pn–pt', description: 'Zmiana ranna od poniedziałku do piątku, weekend wolny' },
      afternoonWeekdays: { label: 'Popołudniowa, pn–pt', description: 'Zmiana popołudniowa od poniedziałku do piątku, weekend wolny' },
      morningAllWeek: { label: 'Ranna, cały tydzień', description: 'Zmiana ranna przez wszystkie 7 dni' },
      afternoonAllWeek: { label: 'Popołudniowa, cały tydzień', description: 'Zmiana popołudniowa przez wszystkie 7 dni' },
      nightAllWeek: { label: 'Nocna, cały tydzień', description: 'Zmiana nocna przez wszystkie 7 dni' },
      rotation: { label: 'Rotacja ranna / popołudniowa', description: 'Dwie ekipy pn–pt: pierwsza połowa listy ranna, druga popołudniowa' },
      noHolidays: { label: 'Bez weekendów i świąt', description: 'Zmiana ranna pn–pt z pominięciem świąt ustawowych' },
      weekendDay: { label: 'Dyżur weekendowy', description: 'Zmiana dzienna w sobotę i niedzielę' },
    },
  },
  empty: {
    team: 'Brak osób do zaplanowania',
    teamHint: 'Lista osób wynika z zakresu danych Twojej roli.',
    filtered: 'Nikt nie pasuje do filtrów',
    filteredHint: 'W trybie „Pracujący w zakresie” widać tylko osoby z wpisem w tym tygodniu.',
    showAll: 'Pokaż wszystkich',
  },
  common: { close: 'Zamknij', delete: 'Usuń', durationHM: '{h} h {m} min' },
  setup: {
    description: 'Definicje czynności do grafiku i atrybuty zmian (np. rejon, sprzęt)',
    hero: { templates: 'Definicji czynności', active: 'Aktywnych', attributes: 'Atrybutów zmian' },
    tabs: { templates: 'Definicje czynności', attributes: 'Atrybuty zmian' },
    search: 'Szukaj definicji…',
    sortOrder: 'Kolejność',
    moveUp: 'Przesuń wyżej',
    moveDown: 'Przesuń niżej',
    edit: 'Edytuj: {name}',
    delete: 'Usuń: {name}',
    deleted: 'Usunięto',
    colors: {
      slate: 'Grafitowy',
      blue: 'Niebieski',
      teal: 'Morski',
      green: 'Zielony',
      amber: 'Bursztynowy',
      orange: 'Pomarańczowy',
      red: 'Czerwony',
      violet: 'Fioletowy',
      pink: 'Różowy',
    },
    templates: {
      add: 'Nowa definicja',
      titleNew: 'Nowa definicja czynności',
      titleEdit: 'Edycja definicji czynności',
      empty: 'Brak definicji czynności',
      emptyHint: 'Definicje to gotowe czynności (np. „Odprawa”, „Załadunek”), które kierownik dodaje do zmian jednym kliknięciem.',
      uncategorized: 'Bez kategorii',
      name: 'Nazwa',
      namePh: 'Nazwa czynności',
      nameRequired: 'Podaj nazwę',
      category: 'Kategoria',
      categoryPh: 'Np. Magazyn, Sprzedaż',
      taskType: 'Typ czynności',
      duration: 'Czas trwania (min)',
      schedule: 'Harmonogram',
      defaultStart: 'Stała godzina',
      offset: 'Przesunięcie (min)',
      color: 'Kolor',
      icon: 'Ikona',
      requiresVehicle: 'Wymaga pojazdu',
      asksForNote: 'Pyta o opis',
      notePrompt: 'Pytanie o opis',
      notePromptPh: 'Np. Ile palet załadowano?',
      checklist: 'Checklista',
      checklistCount: 'Checklista: {n}',
      requiresNote: 'wymaga notatki',
      instructions: 'Instrukcja dla pracownika',
      active: 'Aktywna',
      saved: 'Definicja zapisana',
      deleteTitle: 'Usunąć definicję?',
      confirmDelete: 'Definicja „{name}” zniknie z palety. Jeśli jest użyta w grafiku, zostanie tylko wyłączona.',
    },
    attributes: {
      intro: 'Atrybuty to dodatkowe pola zmiany, które kierownik wypełnia w grafiku (lista wartości, pojazd z Floty albo tekst).',
      add: 'Nowy atrybut',
      titleNew: 'Nowy atrybut zmiany',
      titleEdit: 'Edycja atrybutu zmiany',
      empty: 'Brak atrybutów zmian',
      label: 'Nazwa',
      labelRequired: 'Podaj nazwę',
      code: 'Kod techniczny',
      source: 'Źródło wartości',
      sourceHint: {
        LIST: 'Wybór z własnej listy (np. rejon obsługi)',
        VEHICLE: 'Drugi pojazd z kartoteki Floty',
        TEXT: 'Dowolny tekst, bez podpowiedzi',
      },
      options: 'Wartości listy',
      optionsCount: 'wartości: {n}',
      optionLabel: 'Wartość listy',
      optionPh: 'Nowa wartość',
      optionAdd: 'Dodaj wartość',
      optionRemove: 'Usuń wartość: {label}',
      optionsRequired: 'Lista musi mieć co najmniej jedną wartość',
      builtin: 'Wbudowany',
      builtinHint: 'Wbudowany atrybut pojazdu — można zmienić nazwę, kolejność i widoczność, ale nie można go usunąć.',
      active: 'Widoczny w grafiku',
      saved: 'Atrybut zapisany',
      deleteTitle: 'Usunąć atrybut?',
      confirmDelete: 'Atrybut „{name}” zniknie z okna zmiany; zapisane wartości przestaną być widoczne.',
    },
  },
};

export const plnGridEn: Shape<typeof plnGridPl> = {
  hero: {
    description: 'Week {week} · {range}',
    shifts: 'Shifts this week',
    draftsHint: 'drafts: {n}',
    hours: 'Working hours',
    tasks: 'Activities',
    tasksHint: 'done: {pct}%',
    warnings: 'To review',
    warningsHint: 'conflicts: {conflicts} · working time: {wt}',
    people: 'People',
    absences: 'Absences',
    pending: 'Requests to decide',
  },
  actions: {
    addShift: 'Add shift',
    absence: 'Absence',
    templates: 'Week templates',
    cloneWeek: 'Clone week',
    requests: 'Requests',
    setup: 'Activities & attributes',
    readOnly: 'View only',
  },
  views: { label: 'View', week: 'Week', day: 'Day', month: 'Month', board: 'Activity board' },
  nav: { prev: 'Back', next: 'Next', today: 'Today', week: 'Wk {n}' },
  filters: {
    search: 'Search person…',
    department: 'Department',
    allDepartments: 'All departments',
    people: 'People list',
    peopleWorking: 'Working in range',
    peopleAll: 'Everyone',
    peopleCount: '{shown} of {total}',
    shiftFilter: 'Entry type',
    all: 'All',
    absences: 'Absences',
  },
  short: { MORNING: 'M', AFTERNOON: 'A', NIGHT: 'N', FULL_DAY: 'D' },
  week: {
    people: 'People',
    coverage: 'Coverage',
    noShifts: 'no shifts',
    holiday: 'Holiday',
    selectAll: 'Select all cells',
    clearAll: 'Clear selection',
    selectRow: 'Select person: {name}',
    selectCol: 'Select day: {day}',
    selectShift: 'Select shift {range}',
    addInCell: 'Add shift: {name}, {day}',
    addAbsence: 'Absence',
    moreTasks: '+ {n} more',
  },
  wtBadge: 'Working time: {n}',
  day: {
    carried: 'from previous day',
    dragMove: 'Drag to move the shift',
    dragResize: 'Drag to change the shift length',
    taskDone: 'mark as done',
    taskUndone: 'undo done',
  },
  month: {
    weekNo: 'Wk',
    openDay: '{day}: {n} shifts — open day',
    openWeek: 'Open week {n}',
  },
  board: {
    person: 'Person',
    allPeople: 'Everyone',
    count: 'Activities: {n}',
    empty: 'No activities',
    columns: { PENDING: 'To do', IN_PROGRESS: 'In progress', DONE: 'Done', OTHER: 'Skipped and missed' },
  },
  bulk: {
    label: 'Selection actions',
    cells: '{people} people × {days} days → {cells} cells',
    selectedShifts: 'Selected shifts: {n}',
    insert: 'Insert shift',
    retype: 'Change type',
    delete: 'Delete',
    deleteInCells: 'Delete shifts',
    clear: 'Clear',
    deleteTitle: 'Delete shifts?',
    confirmDelete: '{n} shifts will be removed from the schedule together with their activities.',
    noEmpty: 'There are no empty cells in the selection',
    noShifts: 'There are no shifts to delete in the selection',
  },
  result: {
    saved: 'Shift saved',
    deletedOne: 'Shift deleted',
    created: 'Shifts added: {n}',
    deleted: 'Shifts deleted: {n}',
    updated: 'Shifts changed: {n}',
    skipped: 'Skipped: {n} ({reasons})',
    warnings: 'Saved with working-time warnings: {n}',
    nothing: 'Nothing changed',
    nothingToClone: 'There are no shifts to clone this week',
    cloneAllOccupied: 'Next week already has entries in all these cells — nothing copied',
    absenceSaved: 'Absence saved',
    absenceSavedRemoved: 'Absence saved · shifts taken off the schedule: {n}',
    absenceDeleted: 'Absence deleted',
    absenceDeletedRestored: 'Absence deleted · shifts restored: {n}',
    saveError: 'Could not save — please try again',
  },
  conflict: {
    VEHICLE_DOUBLE: 'Vehicle assigned to two shifts at the same time',
    PERSON_OVERLAP: 'This person has overlapping shifts',
    ABSENCE: 'Shift on a day of absence',
    TASK_OUTSIDE_SHIFT: 'Activity falls outside the shift hours',
    TASK_OVERLAP: 'Activities in the shift overlap',
  },
  conflictShort: {
    VEHICLE_DOUBLE: 'Vehicle',
    PERSON_OVERLAP: '2× shift',
    ABSENCE: 'Absence',
    TASK_OUTSIDE_SHIFT: 'Outside shift',
    TASK_OVERLAP: 'Overlap',
  },
  block: {
    intro: 'The server stopped this save. Change the hours or save anyway — the reason goes to the change log.',
    placeholder: 'E.g. covering for a sick colleague, agreed with the manager',
    required: 'Give a reason (at least 3 characters)',
    submit: 'Save despite the breach',
    change: 'Go back and fix',
  },
  shift: {
    titleNew: 'New shift',
    titleEdit: 'Edit shift',
    titleView: 'Shift',
    person: 'Person',
    personPick: 'Choose a person',
    date: 'Day',
    type: 'Shift type',
    start: 'Start',
    end: 'End',
    nextDay: '(next day)',
    status: 'Status',
    duration: 'Shift length: {h}:{m} h',
    fitHours: 'Fit to activities',
    vehicle: 'Vehicle',
    noVehicle: 'No vehicle',
    attrNone: '— none —',
    attrTextPh: 'Enter a value',
    notes: 'Note',
    notesPh: 'Information for the employee (visible in the app)',
    hints: 'Schedule check',
    hintsOk: 'All clear — rest, working time and assignments are fine',
    save: 'Save shift',
    delete: 'Delete shift',
    confirmDelete: 'The shift will be removed from the schedule together with its activities.',
    copy: 'Copy to other days',
    asAbsence: 'It is an absence',
  },
  task: {
    title: 'Activities',
    empty: 'No activities — add one from a template or manually',
    fromTemplate: 'Add from template…',
    manual: 'Add manually',
    name: 'Name',
    nameRequired: 'Enter the activity name',
    type: 'Type',
    priority: 'Priority',
    start: 'Time',
    duration: 'Duration (min)',
    status: 'Status',
    location: 'Place',
    customer: 'Customer (optional)',
    customerPh: 'Search customer…',
    notes: 'Description',
    checklist: 'Checklist',
    checklistPh: 'New checklist item',
    checklistAdd: 'Add item',
    checklistRemove: 'Remove item: {label}',
    checklistCount: 'checklist {done}/{n}',
    add: 'Add activity',
    update: 'Save activity',
    edit: 'Edit: {title}',
    remove: 'Remove: {title}',
  },
  copy: {
    title: 'Copy shift',
    description: '{range} · activities: {n}',
    person: 'Target person',
    from: 'From',
    to: 'To',
    presets: { nextWeek: 'Next week', restMonth: 'Rest of the month', next30: 'Next 30 days' },
    dows: 'Days of the week',
    count: 'Days to copy: {n}',
    vehicleNote: 'the vehicle is not copied',
    submit: 'Copy ({n})',
    noDays: 'Pick days of the week and a date range',
    allOccupied: 'All chosen days are already taken (shift or absence) — nothing copied',
    skipped: 'Skipped taken days: {n}',
  },
  absence: {
    title: 'New absence',
    detailsTitle: 'Absence',
    from: 'From',
    to: 'To',
    code: 'Type',
    paid: 'Paid — counts towards working time',
    unpaid: 'Unpaid — does not count towards working time',
    notes: 'Notes',
    days: 'Days: {n}',
    clashWarn: 'There are shifts planned on these days ({n}) — they will be taken off the schedule and return when the absence is deleted.',
    save: 'Save absence',
    delete: 'Delete absence',
    confirmDelete: 'The absence will be removed and the shifts taken off at the time will return.',
    fromRequest: 'From an employee request — show requests',
  },
  requests: {
    title: 'Employee requests',
    filter: 'Request status',
    empty: 'No requests',
    preferred: 'preferred: {type}',
    createsAbsence: 'Once approved, the days go to the schedule as: {label}',
    response: 'Response',
    responsePh: 'Response to the employee (optional)',
    respondedBy: 'Decided by: {name}',
    approve: 'Approve',
    reject: 'Reject',
    approved: 'Request approved',
    rejected: 'Request rejected',
    clash: 'This person has shifts planned on these days ({n}). Approving will take them off the schedule.',
    clashConfirm: 'Approve and remove shifts',
  },
  templates: {
    title: 'Week templates',
    description: 'Staff the week {range} with a ready pattern — empty cells only, as drafts.',
    scope: 'For whom',
    scopeVisible: 'Visible people ({n})',
    scopeSelected: 'Selected people ({n})',
    preview: 'To add: {n} · taken cells skipped: {skipped}',
    apply: 'Apply',
    items: {
      morningWeekdays: { label: 'Morning, Mon–Fri', description: 'Morning shift Monday to Friday, weekend off' },
      afternoonWeekdays: { label: 'Afternoon, Mon–Fri', description: 'Afternoon shift Monday to Friday, weekend off' },
      morningAllWeek: { label: 'Morning, whole week', description: 'Morning shift on all 7 days' },
      afternoonAllWeek: { label: 'Afternoon, whole week', description: 'Afternoon shift on all 7 days' },
      nightAllWeek: { label: 'Night, whole week', description: 'Night shift on all 7 days' },
      rotation: { label: 'Morning / afternoon rotation', description: 'Two crews Mon–Fri: first half of the list mornings, second half afternoons' },
      noHolidays: { label: 'No weekends or holidays', description: 'Morning shift Mon–Fri, skipping public holidays' },
      weekendDay: { label: 'Weekend duty', description: 'Day shift on Saturday and Sunday' },
    },
  },
  empty: {
    team: 'No one to schedule',
    teamHint: 'The list of people follows the data scope of your role.',
    filtered: 'No one matches the filters',
    filteredHint: 'In “Working in range” mode only people with an entry this week are shown.',
    showAll: 'Show everyone',
  },
  common: { close: 'Close', delete: 'Delete', durationHM: '{h} h {m} min' },
  setup: {
    description: 'Activity definitions for the schedule and shift attributes (e.g. area, equipment)',
    hero: { templates: 'Activity definitions', active: 'Active', attributes: 'Shift attributes' },
    tabs: { templates: 'Activity definitions', attributes: 'Shift attributes' },
    search: 'Search definitions…',
    sortOrder: 'Order',
    moveUp: 'Move up',
    moveDown: 'Move down',
    edit: 'Edit: {name}',
    delete: 'Delete: {name}',
    deleted: 'Deleted',
    colors: {
      slate: 'Slate',
      blue: 'Blue',
      teal: 'Teal',
      green: 'Green',
      amber: 'Amber',
      orange: 'Orange',
      red: 'Red',
      violet: 'Violet',
      pink: 'Pink',
    },
    templates: {
      add: 'New definition',
      titleNew: 'New activity definition',
      titleEdit: 'Edit activity definition',
      empty: 'No activity definitions',
      emptyHint: 'Definitions are ready-made activities (e.g. “Briefing”, “Loading”) a manager adds to shifts with one click.',
      uncategorized: 'No category',
      name: 'Name',
      namePh: 'Activity name',
      nameRequired: 'Enter a name',
      category: 'Category',
      categoryPh: 'E.g. Warehouse, Sales',
      taskType: 'Activity type',
      duration: 'Duration (min)',
      schedule: 'Scheduling',
      defaultStart: 'Fixed time',
      offset: 'Offset (min)',
      color: 'Colour',
      icon: 'Icon',
      requiresVehicle: 'Needs a vehicle',
      asksForNote: 'Asks for a description',
      notePrompt: 'Description prompt',
      notePromptPh: 'E.g. How many pallets were loaded?',
      checklist: 'Checklist',
      checklistCount: 'Checklist: {n}',
      requiresNote: 'needs a note',
      instructions: 'Instructions for the employee',
      active: 'Active',
      saved: 'Definition saved',
      deleteTitle: 'Delete definition?',
      confirmDelete: 'The definition “{name}” will disappear from the palette. If it is used in the schedule, it will only be deactivated.',
    },
    attributes: {
      intro: 'Attributes are extra shift fields a manager fills in on the schedule (list of values, fleet vehicle or text).',
      add: 'New attribute',
      titleNew: 'New shift attribute',
      titleEdit: 'Edit shift attribute',
      empty: 'No shift attributes',
      label: 'Name',
      labelRequired: 'Enter a name',
      code: 'Technical code',
      source: 'Value source',
      sourceHint: {
        LIST: 'Pick from your own list (e.g. service area)',
        VEHICLE: 'A second vehicle from the Fleet register',
        TEXT: 'Free text, no suggestions',
      },
      options: 'List values',
      optionsCount: 'values: {n}',
      optionLabel: 'List value',
      optionPh: 'New value',
      optionAdd: 'Add value',
      optionRemove: 'Remove value: {label}',
      optionsRequired: 'The list needs at least one value',
      builtin: 'Built-in',
      builtinHint: 'Built-in vehicle attribute — you can rename, reorder and hide it, but not delete it.',
      active: 'Visible on the schedule',
      saved: 'Attribute saved',
      deleteTitle: 'Delete attribute?',
      confirmDelete: 'The attribute “{name}” will disappear from the shift window; saved values will no longer be shown.',
    },
  },
};
